<template>
  <component
    :is="tag"
    ref="element"
    :class="classes"
    :style="styles"
    :type="type"
    :disabled="loading || null"
    v-bind="$attrs"
    @click="onClick"
  >
    <div v-if="loading">
      <base-loader />
    </div>
    <slot v-else />
  </component>
</template>

<script setup>
import { ref, computed, useAttrs } from 'vue'
import BaseLoader from '@/packages/BaseLoader'

/**
 * @typedef {Object} Props
 * @property {string} size - The size of the element (sm, md)
 * @property {boolean} primary - Indicates if the element should have a primary color
 * @property {boolean} secondary - Indicates if the element should have a secondary color
 * @property {boolean} strokePrimary - Indicates if the element should have a primary stroke color
 * @property {boolean} strokeSecondary - Indicates if the element should have a secondary stroke color
 * @property {boolean} black - Indicates if the element should have a black color
 * @property {boolean} white - Indicates if the element should have a white color
 * @property {boolean} light - Indicates if the element should have a light color
 * @property {boolean} error - Indicates if the element should display an error
 * @property {boolean} iconOnly - Indicates if the element will only have an icon
 * @property {boolean} rounded - Indicates if the element should have rounded borders
 * @property {boolean} loading - Indicates if the element should display a loading state
 * @property {boolean} spaceBetween - Indicates if the element should have space between its contents
 * @property {string} m - The margin of the element
 */
const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v.toLowerCase()),
  },
  type: {
    type: String,
    default: 'cta',
    validator: (v) =>
      ['cta', 'monochrome', 'shade', 'error'].includes(v.toLowerCase()),
  },
  outline: {
    type: Boolean,
    default: false,
  },
  link: {
    type: Boolean,
    default: false,
  },
  submit: {
    type: Boolean,
    default: false,
  },
  reset: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  iconOnly: Boolean,
  primary: Boolean,
  secondary: Boolean,
  strokePrimary: Boolean,
  strokeSecondary: Boolean,
  black: Boolean,
  white: Boolean,
  light: Boolean,
  error: Boolean,
  spaceBetween: Boolean,
  m: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['click', 'onClick'])

const attrs = useAttrs()
const element = ref(null)

const classes = computed(() => {
  const baseClasses = {
    'base-btn': true,
    [`base-btn--${props.size}`]: true,
    'base-btn--cta': props.type.toLowerCase() === 'cta' && !props.outline,
    'base-btn--ctaOutline': props.type.toLowerCase() === 'cta' && props.outline,
    'base-btn--shade': props.type.toLowerCase() === 'shade' && !props.outline,
    'base-btn--shadeOutline':
      props.type.toLowerCase() === 'shade' && props.outline,
    'base-btn--monochrome':
      props.type.toLowerCase() === 'monochrome' && !props.outline,
    'base-btn--monochromeOutline':
      props.type.toLowerCase() === 'monochrome' && props.outline,
    'base-btn--error': props.type.toLowerCase() === 'error' && !props.outline,
    'base-btn--errorOutline':
      props.type.toLowerCase() === 'error' && props.outline,
    'base-btn--loading': props.loading,
    'base-btn--link': props.link,
    'base-btn--icon': props.icon,
    'base-btn--rounded': props.rounded,
  }

  // checking legacy props
  switch (true) {
    case !!props.primary:
      console.warn(
        'BaseButton: prop "primary" is depricated, use prop: type="cta"'
      )
      baseClasses['base-btn--cta'] = true
      break
    case !!props.secondary:
      console.warn(
        'BaseButton: prop "secondary" is depricated, use prop: type="shade"'
      )
      baseClasses['base-btn--shade'] = true
      baseClasses['base-btn--cta'] = false
      break
    case !!props.strokePrimary:
      console.warn(
        'BaseButton: prop "stroke-primary" is depricated, use props: type="cta" outline'
      )
      baseClasses['base-btn--ctaOutline'] = true
      baseClasses['base-btn--cta'] = false
      break
    case !!props.strokeSecondary:
      console.warn(
        'BaseButton: prop "stroke-secondary" is depricated, use props: type="shade" outline'
      )
      baseClasses['base-btn--shadeOutline'] = true
      baseClasses['base-btn--cta'] = false
      break
    case !!props.black:
      console.warn(
        'BaseButton: prop "black" is depricated, use prop: type="shade"'
      )
      baseClasses['base-btn--shade'] = true
      baseClasses['base-btn--cta'] = false
      break
    case !!props.white:
      console.warn(
        'BaseButton: prop "white" is depricated, use prop: type="monochrome"'
      )
      baseClasses['base-btn--monochrome'] = true
      baseClasses['base-btn--cta'] = false
      break
    case !!props.light:
      console.warn(
        'BaseButton: prop "light" is depricated, use prop: type="monochrome"'
      )
      baseClasses['base-btn--monochrome'] = true
      baseClasses['base-btn--cta'] = false
      break
    case !!props.error:
      console.warn(
        'BaseButton: prop "error" is depricated, use prop: type="error"'
      )
      baseClasses['base-btn--error'] = true
      baseClasses['base-btn--cta'] = false
      break
    case !!props.iconOnly:
      console.warn('BaseButton: prop "iconOnly" is depricated, use prop: icon')
      baseClasses['base-btn--icon'] = true
      break
    case !!props.spaceBetween:
      console.warn('BaseButton: prop "space-between" is depricated')
      break
    case !!props.m:
      console.warn('BaseButton: prop "m" is depricated')
      break
  }

  return baseClasses
})

const tag = computed(() => {
  if (attrs?.href) return 'a'
  if (attrs?.to) return 'router-link'
  return 'button'
})

const type = computed(() => {
  if (props.submit) return 'submit'
  if (props.reset) return 'reset'
  return 'button'
})

const styles = computed(() => {
  const extraStyles = { margin: props.m }
  if (props.loading && !!element.value?.offsetWidth) {
    extraStyles.width = `${element.value.offsetWidth}px`
  }
  return extraStyles
})

function onClick(event) {
  emit('click', event)
  emit('onClick', event)
}
</script>

<style lang="scss">
:root {
  --base-button-bg-color-active: transparent;
  --base-button-bg-color-default: transparent;
  --base-button-bg-color-disabled: var(--bg-disabled);
  --base-button-bg-color-focus: transparent;
  --base-button-bg-color-hover: transparent;
  --base-button-border-active: 1px solid transparent;
  --base-button-border-default: 1px solid transparent;
  --base-button-border-disabled: transparent;
  --base-button-border-focus: 1px solid transparent;
  --base-button-border-hover: 1px solid transparent;
  --base-button-border-radius: var(--radius); // 12px
  --base-button-cursor-default: pointer;
  --base-button-font-family: var(--sans);
  --base-button-font-size: var(--level-6); // 1rem / 16px
  --base-button-font-weight: 500;
  --base-button-gap: 0.75rem; // 12px
  --base-button-height: 3.5rem; // 56px
  --base-button-icon-height: 1.5rem; // 24px
  --base-button-icon-width: 1.5rem; // 24px
  --base-button-leading: 1.45;
  --base-button-padding-x: 1.5rem; // 24px
  --base-button-padding-y: 1rem; // 16px
  --base-button-pointer-events-disabled: auto; // 16px
  --base-button-svg-color-fill: currentColor;
  --base-button-text-color-active: inherit;
  --base-button-text-color-default: inherit;
  --base-button-text-color-disabled: var(--text-5);
  --base-button-text-color-focus: inherit;
  --base-button-text-color-hover: inherit;
  --base-button-transition: all 0.35s ease;
  --base-button-width-default: 100%;
  --base-button-width-min: fit-content;
}

.base-btn {
  background-color: var(--base-button-bg-color-default);
  border-radius: var(--base-button-border-radius);
  border: var(--base-button-border-default);
  color: var(--base-button-text-color-default);
  font-family: var(--base-button-font-family);
  font-size: var(--base-button-font-size);
  font-weight: var(--base-button-font-weight);
  gap: var(--base-button-gap);
  height: var(--base-button-height);
  line-height: var(--base-button-leading);
  min-width: var(--base-button-width-min);
  padding: var(--base-button-padding-y) var(--base-button-padding-x);
  transition: var(--base-button-transition);
  width: var(--base-button-width-default);
  cursor: var(--base-button-cursor-default);

  @apply outline-none focus:outline-none box-border;
  @apply inline-flex items-center justify-center;

  @media (min-width: 720px) {
    --base-button-width-default: auto;
  }

  //* Общие состояния
  &:hover {
    background-color: var(--base-button-bg-color-hover);
    border: var(--base-button-border-hover);
    color: var(--base-button-text-color-hover);
  }

  &:active {
    background-color: var(--base-button-bg-color-active);
    border: var(--base-button-border-active);
    color: var(--base-button-text-color-active);
  }

  &:focus-visible {
    background-color: var(--base-button-bg-color-focus);
    border: var(--base-button-border-focus);
    color: var(--base-button-text-color-focus);
  }

  &:focus:not(:focus-visible):not(:active):not(:hover) {
    background-color: var(--base-button-bg-color-default);
    border: var(--base-button-border-default);
    color: var(--base-button-text-color-default);
    outline: none;
  }

  &:disabled,
  &[disabled] {
    --base-button-cursor-default: not-allowed;

    border: var(--base-button-border-disabled);
    background-color: var(--base-button-bg-color-disabled) !important;
    color: var(--base-button-text-color-disabled);
    pointer-events: var(--base-button-pointer-events-disabled);

    &:active {
      --base-button-pointer-events-disabled: none;
    }
  }

  &--loading {
    --base-button-cursor-default: wait !important;
  }

  //* icons
  > .base-icon, // legacy class
  > svg {
    width: var(--base-button-icon-width);
    height: var(--base-button-icon-height);

    path {
      fill: var(--base-button-svg-color-fill);
    }
  }

  .base-icon {
    // legacy class
    svg {
      --base-button-icon-width: 100%;
      --base-button-icon-height: 100%;
    }
  }

  //* Размеры
  &--sm {
    --base-button-height: 2.5rem; // 40px
    --base-button-border-radius: 0.5rem; // 8px
    --base-button-font-size: var(--level-7);
    --base-button-padding-x: 1rem; // 16px
    --base-button-padding-y: 0.5rem; // 8px

    > .base-icon,
    > svg {
      --base-button-icon-width: 1rem; // 16px
      --base-button-icon-height: 1rem; // 16px
    }
  }

  &--lg {
    @media (min-width: 720px) {
      --base-button-width-default: 100%;
    }
  }

  //* Variations:
  // CTA
  &--cta {
    --base-button-bg-color-active: var(--brand-active);
    --base-button-bg-color-default: var(--brand);
    --base-button-bg-color-focus: var(--brand-active);
    --base-button-bg-color-hover: var(--brand-hover);
    --base-button-text-color-active: #111;
    --base-button-text-color-default: #111;
    --base-button-text-color-focus: #111;
    --base-button-text-color-hover: #111;
  }

  // Shade
  &--shade {
    --base-button-bg-color-active: var(--bg-2);
    --base-button-bg-color-default: var(--bg-3);
    --base-button-bg-color-focus: #373737;
    --base-button-bg-color-hover: #373737;
    --base-button-text-color-active: var(--text-1);
    --base-button-text-color-default: var(--text-1);
    --base-button-text-color-focus: var(--text-1);
    --base-button-text-color-hover: var(--text-1);
  }

  // Monochrome
  &--monochrome {
    --base-button-bg-color-active: #e0e0e0;
    --base-button-bg-color-default: var(--text-1);
    --base-button-bg-color-focus: var(--text-1);
    --base-button-bg-color-hover: var(--text-2);
    --base-button-text-color-active: var(--bg-1);
    --base-button-text-color-default: var(--bg-1);
    --base-button-text-color-focus: var(--bg-1);
    --base-button-text-color-hover: var(--bg-1);
  }

  // Error
  &--error {
    --base-button-bg-color-active: #e53935;
    --base-button-bg-color-default: #e53935;
    --base-button-bg-color-focus: #e53935;
    --base-button-bg-color-hover: #e53935;
    --base-button-text-color-active: var(--text-1);
    --base-button-text-color-default: var(--text-1);
    --base-button-text-color-focus: var(--text-1);
    --base-button-text-color-hover: var(--text-1);
  }

  // CTA outline
  &--ctaOutline {
    --base-button-border-default: 1px solid var(--brand);
    --base-button-border-focus: 1px solid var(--brand);
    --base-button-border-hover: 1px solid var(--brand);
    --base-button-border-active: 1px solid var(--brand);
    --base-button-text-color-active: #7d0cfa;
    --base-button-text-color-default: var(--brand);
    --base-button-text-color-focus: #a061fe;
    --base-button-text-color-hover: #a061fe;
  }

  // Shade outline
  &--shadeOutline {
    --base-button-border-default: 1px solid var(--bg-35);
    --base-button-border-focus: 1px solid #373737;
    --base-button-border-hover: 1px solid #373737;
    --base-button-border-active: 1px solid var(--bg-2);
    --base-button-text-color-active: var(--text-1);
    --base-button-text-color-default: var(--text-1);
    --base-button-text-color-focus: var(--text-1);
    --base-button-text-color-hover: var(--text-1);
  }

  // Monochrome outline
  &--monochromeOutline {
    --base-button-border-default: 1px solid var(--text-1);
    --base-button-border-focus: 1px solid var(--text-1);
    --base-button-border-hover: 1px solid var(--text-1);
    --base-button-border-active: 1px solid #e0e0e0;
    --base-button-text-color-active: var(--text-1);
    --base-button-text-color-default: var(--text-1);
    --base-button-text-color-focus: var(--text-1);
    --base-button-text-color-hover: var(--text-1);
  }

  // Error outline
  &--errorOutline {
    --base-button-border-default: 1px solid var(--error);
    --base-button-border-focus: 1px solid #ef5350;
    --base-button-border-hover: 1px solid #ef5350;
    --base-button-border-active: 1px solid var(--error-hover);
    --base-button-text-color-active: var(--error-hover);
    --base-button-text-color-default: var(--error);
    --base-button-text-color-focus: #ef5350;
    --base-button-text-color-hover: #ef5350;
  }

  // Link
  &--link {
    --base-button-bg-color-active: transparent;
    --base-button-bg-color-default: transparent;
    --base-button-bg-color-disabled: transparent;
    --base-button-bg-color-focus: transparent;
    --base-button-bg-color-hover: transparent;
    --base-button-border-disabled: transparent;
    --base-button-font-weight: 500;
    --base-button-gap: 0.25rem; // 4px
    --base-button-height: auto;
    --base-button-padding-x: 0;
    --base-button-padding-y: 0;
    --base-button-pointer-events-disabled: none;
    --base-button-text-color-active: #7d0cfa;
    --base-button-text-color-default: var(--brand);
    --base-button-text-color-focus: #a061fe;
    --base-button-text-color-hover: #a061fe;
    --base-button-width-default: auto;
  }

  //* for icons only
  &--icon {
    --base-button-padding-x: 0.94rem;

    &.base-btn--sm {
      --base-button-padding-x: 0.69rem;
    }
  }

  //* for icons + rounded button
  &--rounded {
    --base-button-border-radius: 100%;
  }
}
</style>
